import {
  Assessment as AssessmentIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  Download as DownloadIcon,
  Inventory as InventoryIcon,
  LocalOffer as LocalOfferIcon,
  LocalShipping as LocalShippingIcon,
  Payment as PaymentIcon,
  People as PeopleIcon,
  Receipt as ReceiptIcon,
  TableChart as TableChartIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useEstimateRowCount, useExportReport, useReportColumns } from "../../hooks/useReports";
import { ExportReportRequest, REPORT_TYPES, ReportType } from "../../types/reports";
import { formatApiError } from "../../utils/formatApiError";
import AdminPage from "./AdminPage";

const REPORT_ICONS: Record<string, React.ReactElement> = {
  receipt: <ReceiptIcon />,
  inventory: <InventoryIcon />,
  table_chart: <TableChartIcon />,
  payment: <PaymentIcon />,
  local_shipping: <LocalShippingIcon />,
  people: <PeopleIcon />,
  local_offer: <LocalOfferIcon />,
};

const ReportsPage: React.FC = () => {
  const { t } = useTranslation();

  // State
  const [selectedReport, setSelectedReport] = useState<ReportType | null>(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [status, setStatus] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("");
  const [paymentMethodType, setPaymentMethodType] = useState("");
  const [retailer, setRetailer] = useState("");
  const [includeArchived, setIncludeArchived] = useState(false);
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [estimatedRows, setEstimatedRows] = useState<number | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" as "success" | "error" });
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
  const [showColumns, setShowColumns] = useState(false);

  const exportMutation = useExportReport();
  const estimateMutation = useEstimateRowCount();
  const { data: availableColumns, isLoading: columnsLoading } = useReportColumns(selectedReport);

  // Reset column selection when available columns change (new report type loaded)
  useEffect(() => {
    if (availableColumns) {
      setSelectedColumns(availableColumns.map((c) => c.key));
    }
  }, [availableColumns]);

  // Memoize a Set for O(1) lookup in checkbox render
  const selectedColumnsSet = useMemo(() => new Set(selectedColumns), [selectedColumns]);
  const allColumnsSelected = availableColumns ? selectedColumns.length === availableColumns.length : true;
  const noColumnsSelected = selectedColumns.length === 0;

  const buildRequest = useCallback((): ExportReportRequest | null => {
    if (!selectedReport) return null;
    // Only send selectedColumns if not all are selected (optimization)
    const columnsToSend = availableColumns && selectedColumns.length < availableColumns.length
      ? selectedColumns
      : undefined;
    return {
      reportType: selectedReport,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      status: status || undefined,
      paymentStatus: paymentStatus || undefined,
      paymentMethodType: paymentMethodType || undefined,
      retailer: retailer || undefined,
      includeArchived,
      includeDeleted,
      selectedColumns: columnsToSend,
    };
  }, [selectedReport, startDate, endDate, status, paymentStatus, paymentMethodType, retailer, includeArchived, includeDeleted, selectedColumns, availableColumns]);

  const handleEstimate = useCallback(async () => {
    const request = buildRequest();
    if (!request) return;
    try {
      const count = await estimateMutation.mutateAsync(request);
      setEstimatedRows(count);
    } catch (error) {
      setSnackbar({ open: true, message: formatApiError(error), severity: "error" });
    }
  }, [buildRequest, estimateMutation, t]);

  const handleExport = useCallback(async () => {
    const request = buildRequest();
    if (!request) return;
    try {
      await exportMutation.mutateAsync(request);
      setSnackbar({ open: true, message: t("admin.reports.downloadReady"), severity: "success" });
    } catch (error) {
      setSnackbar({ open: true, message: formatApiError(error), severity: "error" });
    }
  }, [buildRequest, exportMutation, t]);

  const handleReportSelect = useCallback((type: ReportType) => {
    setSelectedReport(type);
    setEstimatedRows(null);
    setSelectedColumns([]);
    setShowColumns(false);
  }, []);

  const handleToggleColumn = useCallback((key: string) => {
    setSelectedColumns((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  }, []);

  const handleSelectAllColumns = useCallback(() => {
    if (!availableColumns) return;
    if (allColumnsSelected) {
      setSelectedColumns([]);
    } else {
      setSelectedColumns(availableColumns.map((c) => c.key));
    }
  }, [availableColumns, allColumnsSelected]);

  const selectedReportInfo = useMemo(
    () => REPORT_TYPES.find((r) => r.type === selectedReport),
    [selectedReport]
  );

  return (
    <AdminPage>
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 1 }}>
        <AssessmentIcon sx={{ fontSize: 32, color: "primary.main" }} />
        <Box>
          <Typography variant="h5" fontWeight="bold">
            {t("admin.reports.title")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("admin.reports.description")}
          </Typography>
        </Box>
      </Box>

      {/* Report Type Selector */}
      <Typography variant="h6" sx={{ mb: 2 }}>
        {t("admin.reports.selectReportType")}
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 3 }}>
        {REPORT_TYPES.map((report) => {
          const isSelected = selectedReport === report.type;
          return (
            <Card
              key={report.type}
              variant={isSelected ? "elevation" : "outlined"}
              sx={{
                width: { xs: "100%", sm: "calc(50% - 12px)", md: "calc(25% - 12px)", lg: "calc(14.28% - 12px)" },
                minWidth: 140,
                border: isSelected ? 2 : 1,
                borderColor: isSelected ? "primary.main" : "divider",
                transition: "all 0.2s",
              }}
            >
              <CardActionArea onClick={() => handleReportSelect(report.type)}>
                <CardContent sx={{ textAlign: "center", py: 1.5, px: 1, "&:last-child": { pb: 1.5 } }}>
                  <Box sx={{ color: isSelected ? "primary.main" : "text.secondary", mb: 0.5 }}>
                    {REPORT_ICONS[report.icon] || <ReceiptIcon />}
                  </Box>
                  <Typography variant="caption" fontWeight="bold" sx={{ display: "block", lineHeight: 1.3 }}>
                    {t(report.labelKey)}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          );
        })}
      </Box>

      {/* Filters */}
      {selectedReport && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {t("admin.reports.filters")}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label={t("admin.reports.startDate")}
                type="date"
                value={startDate}
                onChange={(e) => { setStartDate(e.target.value); setEstimatedRows(null); }}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label={t("admin.reports.endDate")}
                type="date"
                value={endDate}
                onChange={(e) => { setEndDate(e.target.value); setEstimatedRows(null); }}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>{t("admin.reports.filterStatus")}</InputLabel>
                <Select
                  value={status}
                  label={t("admin.reports.filterStatus")}
                  onChange={(e) => { setStatus(e.target.value); setEstimatedRows(null); }}
                >
                  <MenuItem value="">{t("admin.reports.all")}</MenuItem>
                  <MenuItem value="1">{t("admin.reports.statusCreated")}</MenuItem>
                  <MenuItem value="6">{t("admin.reports.statusProcessing")}</MenuItem>
                  <MenuItem value="7">{t("admin.reports.statusPendingVerification")}</MenuItem>
                  <MenuItem value="8">{t("admin.reports.statusHandedToShipping")}</MenuItem>
                  <MenuItem value="11">{t("admin.reports.statusDelivered")}</MenuItem>
                  <MenuItem value="12">{t("admin.reports.statusCancelled")}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>{t("admin.reports.filterPaymentStatus")}</InputLabel>
                <Select
                  value={paymentStatus}
                  label={t("admin.reports.filterPaymentStatus")}
                  onChange={(e) => { setPaymentStatus(e.target.value); setEstimatedRows(null); }}
                >
                  <MenuItem value="">{t("admin.reports.all")}</MenuItem>
                  <MenuItem value="succeeded">Succeeded</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="pending_verification">Pending Verification</MenuItem>
                  <MenuItem value="failed">Failed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>{t("admin.reports.filterPaymentMethod")}</InputLabel>
                <Select
                  value={paymentMethodType}
                  label={t("admin.reports.filterPaymentMethod")}
                  onChange={(e) => { setPaymentMethodType(e.target.value); setEstimatedRows(null); }}
                >
                  <MenuItem value="">{t("admin.reports.all")}</MenuItem>
                  <MenuItem value="1">Stripe</MenuItem>
                  <MenuItem value="2">Alif</MenuItem>
                  <MenuItem value="3">DC Card</MenuItem>
                  <MenuItem value="4">PayPal</MenuItem>
                  <MenuItem value="5">Bank Transfer</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>{t("admin.reports.filterRetailer")}</InputLabel>
                <Select
                  value={retailer}
                  label={t("admin.reports.filterRetailer")}
                  onChange={(e) => { setRetailer(e.target.value); setEstimatedRows(null); }}
                >
                  <MenuItem value="">{t("admin.reports.all")}</MenuItem>
                  <MenuItem value="amazon">Amazon</MenuItem>
                  <MenuItem value="walmart">Walmart</MenuItem>
                  <MenuItem value="costco">Costco</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeArchived}
                    onChange={(e) => { setIncludeArchived(e.target.checked); setEstimatedRows(null); }}
                    size="small"
                  />
                }
                label={t("admin.reports.includeArchived")}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeDeleted}
                    onChange={(e) => { setIncludeDeleted(e.target.checked); setEstimatedRows(null); }}
                    size="small"
                  />
                }
                label={t("admin.reports.includeDeleted")}
              />
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Column Selector */}
      {selectedReport && availableColumns && !columnsLoading && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: showColumns ? 2 : 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="h6">
                {t("admin.reports.columns")}
              </Typography>
              <Chip
                size="small"
                label={`${selectedColumns.length} / ${availableColumns.length}`}
                color={allColumnsSelected ? "default" : "primary"}
              />
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button size="small" onClick={handleSelectAllColumns}>
                {allColumnsSelected ? t("admin.reports.deselectAll") : t("admin.reports.selectAll")}
              </Button>
              <Button size="small" variant="outlined" onClick={() => setShowColumns(!showColumns)}>
                {showColumns ? t("admin.reports.hideColumns") : t("admin.reports.showColumns")}
              </Button>
            </Box>
          </Box>
          <Collapse in={showColumns}>
            {columnsLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0 }}>
                {availableColumns.map((col) => (
                  <FormControlLabel
                    key={col.key}
                    sx={{ width: { xs: "100%", sm: "50%", md: "33.33%", lg: "25%" }, m: 0 }}
                    control={
                      <Checkbox
                        size="small"
                        checked={selectedColumnsSet.has(col.key)}
                        onChange={() => handleToggleColumn(col.key)}
                        icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                        checkedIcon={<CheckBoxIcon fontSize="small" />}
                      />
                    }
                    label={<Typography variant="body2">{col.header}</Typography>}
                  />
                ))}
              </Box>
            )}
          </Collapse>
        </Paper>
      )}

      {/* Actions */}
      {selectedReport && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              onClick={handleEstimate}
              disabled={estimateMutation.isPending}
              startIcon={estimateMutation.isPending ? <CircularProgress size={16} /> : undefined}
            >
              {t("admin.reports.estimateRows")}
            </Button>

            <Button
              variant="contained"
              onClick={handleExport}
              disabled={exportMutation.isPending || noColumnsSelected}
              startIcon={exportMutation.isPending ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
            >
              {exportMutation.isPending ? t("admin.reports.generating") : t("admin.reports.exportExcel")}
            </Button>

            {estimatedRows !== null && (
              <Typography variant="body2" color="text.secondary">
                {t("admin.reports.estimatedRows")}: <strong>{estimatedRows.toLocaleString()}</strong>
              </Typography>
            )}
          </Box>

          {estimatedRows !== null && estimatedRows > 5000 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {t("admin.reports.largeExportWarning")}
            </Alert>
          )}

          {estimatedRows === 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              {t("admin.reports.noDataFound")}
            </Alert>
          )}
        </Paper>
      )}

      {/* Selected report info */}
      {selectedReportInfo && (
        <Paper sx={{ p: 2, bgcolor: "action.hover" }}>
          <Typography variant="body2" color="text.secondary">
            <strong>{t("admin.reports.selectedReport")}:</strong>{" "}
            {t(selectedReportInfo.labelKey)} — {t(selectedReportInfo.descriptionKey)}
          </Typography>
        </Paper>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
    </AdminPage>
  );
};

export default ReportsPage;
